/**
 * Organization allowlist for reply translation (the languages an agent can
 * translate a reply into). Same catalog and label order as the extension
 * picker, via `reply-languages.ts`. This is not an AI Reply `replyLanguage`.
 *
 * null means no restriction. An empty list is stored as null: an empty array
 * must not mean "show no languages".
 */
import { isReplyLanguage, replyLanguageOptionGroups } from './reply-languages';

let catalogOrder: readonly string[] | null = null;

function replyTranslateCatalogOrder(): readonly string[] {
  if (!catalogOrder) {
    const groups = replyLanguageOptionGroups();
    catalogOrder = [...groups.common, ...groups.more].map((option) => option.code);
  }
  return catalogOrder;
}

/** Catalog order. Unknown codes are dropped. Empty becomes null. */
export function normalizeReplyTranslateLanguages(codes: readonly string[]): string[] | null {
  const wanted = new Set<string>();
  for (const code of codes) {
    if (isReplyLanguage(code)) wanted.add(code);
  }
  if (wanted.size === 0) return null;
  const ordered = replyTranslateCatalogOrder().filter((code) => wanted.has(code));
  return ordered.length > 0 ? ordered : null;
}

/** Value from GET /api/org-settings. Missing or unusable data is no restriction. */
export function readStoredReplyTranslateLanguages(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const codes: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') codes.push(item);
  }
  return normalizeReplyTranslateLanguages(codes);
}

/** Body for PUT /api/org-settings. Sends null when the admin is not restricting. */
export function replyTranslateSettingsBody(languages: readonly string[] | null): {
  replyTranslateLanguages: string[] | null;
} {
  if (!languages || languages.length === 0) return { replyTranslateLanguages: null };
  return { replyTranslateLanguages: normalizeReplyTranslateLanguages(languages) };
}
